import {
  Typography,
  TableContainer,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  useTheme,
  Stack,
  Box,
} from '@mui/material';
import React from 'react';
import { Profile } from '@par/shared/types/player';

type PropTypes = {
  viewProfile: Profile;
};

export const LifetimeStats = ({ viewProfile }: PropTypes) => {
  const {
    aves,
    totalMatchGamesPlayed,
    totalTeamGamesPlayed,
    totalSitAndGoGamesPlayed,
    totalSoloGamesPlayed,
    totalDailyGamesPlayed,
    questionsSeenCount,
  } = viewProfile;

  const theme = useTheme();

  const calcPer100 = (aveCount: number, questionsSeenCount: number) =>
    (aveCount / questionsSeenCount) * 100;

  const calcPOB = (aves: Record<string, number>) => {
    let sum = 0;
    for (const key of Object.keys(aves)) {
      if (key !== 'bogey') {
        sum = sum + calcPer100(aves[key], questionsSeenCount);
      }
    }
    return Math.round((sum / 100) * 100) + '%';
  };

  return (
    <>
      <Typography
        id="lifetime-stats"
        variant="h5"
        mb={2}
        sx={{
          textShadow:
            theme.palette.mode === 'dark'
              ? `0 0 0px ${theme.palette.secondary.main}, 0 0 0px ${theme.palette.secondary.main}, 0px 0px 16px ${theme.palette.secondary.main}`
              : 'none',
        }}
      >
        Lifetime Stats
      </Typography>

      <Stack direction={'row'} spacing={2}>
        <Stack
          direction={'column'}
          spacing={2}
          alignItems={'flex-start'}
          justifyContent={'center'}
        >
          {/* POB% */}
          <Box width={'100%'}>
            <Tooltip placement="left" title="PAR or better percentage">
              <Stack
                direction={'row'}
                justifyContent={'space-between'}
                spacing={1}
                sx={{
                  backgroundColor: '#121212',
                  backgroundImage:
                    'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
                  borderRadius: '4px',
                  color: '#fff',
                  padding: '6px 16px',
                }}
              >
                <Typography fontSize={'14px'}>POB %</Typography>
                <Typography fontSize={'14px'}>{calcPOB(aves)}</Typography>
              </Stack>
            </Tooltip>
          </Box>
          {/* Games Played */}
          <Box width={'100%'}>
            <Typography variant="body1" textAlign={'left'} mb={1}>
              Games Played
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small" aria-label="simple table">
                <TableBody>
                  <TableRow>
                    <TableCell>Solo</TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                      {totalSoloGamesPlayed}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Match</TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                      {totalMatchGamesPlayed}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Sit & Go</TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                      {totalSitAndGoGamesPlayed}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Teams</TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                      {totalTeamGamesPlayed || 0}
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>Dailies (subset of Solo)</TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                      {totalDailyGamesPlayed}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Stack>
        {/* Survival */}
        <Box bgcolor={'#ccf49f26'} flex={1} paddingX={2} borderRadius={1}>
          <Typography
            marginBottom={2}
            marginTop={1}
            textTransform={'uppercase'}
            letterSpacing={2}
          >
            Survival
          </Typography>
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            spacing={2}
            marginBottom={2}
          >
            {/* Challenge */}
            <Box width={'100%'}>
              <Typography variant="body1" textAlign={'left'} mb={1}>
                Challenge Tower
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small" aria-label="simple table">
                  <TableBody>
                    <TableRow>
                      <TableCell>Played</TableCell>
                      <TableCell style={{ textAlign: 'right' }}>
                        {viewProfile.survivalStats?.challenge?.allTime
                          ?.gamesPlayed ?? '-'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Most Slain</TableCell>
                      <TableCell style={{ textAlign: 'right' }}>
                        {viewProfile.survivalStats?.challenge?.allTime
                          ?.longestStreak?.questionsSurvived ?? '-'}
                      </TableCell>
                    </TableRow>
                    <TableRow
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell>Best Score</TableCell>
                      <TableCell style={{ textAlign: 'right' }}>
                        {viewProfile.survivalStats?.challenge?.allTime
                          ?.bestScore?.score ?? '-'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            {/* Endurance */}
            <Box width={'100%'}>
              <Typography variant="body1" textAlign={'left'} mb={1}>
                Endurance
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small" aria-label="simple table">
                  <TableBody>
                    <TableRow>
                      <TableCell>Played</TableCell>
                      <TableCell style={{ textAlign: 'right' }}>
                        {viewProfile.survivalStats?.endurance?.gamesPlayed ??
                          '-'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Most Slain</TableCell>
                      <TableCell style={{ textAlign: 'right' }}>
                        {viewProfile.survivalStats?.endurance?.longestStreak
                          ?.questionsSurvived ?? '-'}
                      </TableCell>
                    </TableRow>
                    <TableRow
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell>Best Score</TableCell>
                      <TableCell style={{ textAlign: 'right' }}>
                        {viewProfile.survivalStats?.endurance?.bestScore
                          ?.score ?? '-'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </>
  );
};
